import { useEffect, useMemo, useState } from "react";
import { vocabularize } from "src/Common/vocab";
import { toast } from "react-toastify";

import UserService from "src/Services/API/user.service";
import PolicyService from "src/Services/API/policy.service";
import Loader from "src/Components/Common/Loader";
import RoleDropDown from "./roledropdown";
import { getAvatarUrl } from "src/Common/common";
import {
  can,
  handleError,
  formatUSPhone,
  isValidUSPhone,
  toE164Phone,
  isSuperAdminUser,
} from "src/Common/function";

const INHERITED_PREVIEW_COUNT = 4;

// Real avatar image when available; falls back to an initials circle.
function MemberAvatar({ user, className = "w-12 h-12 text-[16px]" }) {
  const [errored, setErrored] = useState(false);
  if (user?.avatar && !errored) {
    return (
      <img
        src={getAvatarUrl(user.avatar)}
        onError={() => setErrored(true)}
        alt={user?.name ?? ""}
        className={`shrink-0 rounded-full object-cover ${className}`}
      />
    );
  }
  const initial = (user?.name || user?.email || "?")[0].toUpperCase();
  return (
    <span
      className={`shrink-0 rounded-full bg-[#EAF3FF] text-[#0058E5] font-OpenSansSemiBold flex items-center justify-center ${className}`}
    >
      {initial}
    </span>
  );
}

const XIcon = ({ className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" className={className}>
    <path d="M9 3L3 9M3 3l6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Chip-input with an inline search that adds items from `options`.
 * `variant` picks the chip palette.
 */
function ChipSearchInput({
  options,
  selectedIds,
  onAdd,
  onRemove,
  placeholder,
  variant = "brand", // brand (blue) | slate
  readOnly = false,
}) {
  const [query, setQuery] = useState("");

  const selected = useMemo(
    () => selectedIds.map((id) => options.find((o) => o.id === id)).filter(Boolean),
    [options, selectedIds],
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return options
      .filter((o) => !selectedIds.includes(o.id))
      .filter((o) => o.name?.toLowerCase().includes(q))
      .slice(0, 8);
  }, [options, selectedIds, query]);

  const chipClass = variant === "brand" ? "bg-[#EAF3FF] text-[#0058E5]" : "bg-[#EEF0F9] text-[#5F6477]";

  return (
    <div className="relative min-h-[46px] rounded-[8px] outline outline-1 -outline-offset-1 outline-[#E5E7EB] px-3 py-2 flex flex-wrap items-center gap-2 bg-white">
      {selected.map((item) => (
        <span
          key={item.id}
          className={`inline-flex items-center gap-1.5 pl-2.5 pr-2 py-[2px] rounded-[6px] text-[14px] leading-5 tracking-[0.14px] font-OpenSansSemiBold capitalize ${chipClass}`}
        >
          {vocabularize(item.name)}
          {!readOnly && (
            <button
              onClick={() => onRemove(item.id)}
              className="text-[#868DA6] hover:text-[#D93A3A] flex items-center"
              title="Remove"
            >
              <XIcon />
            </button>
          )}
        </span>
      ))}
      {!readOnly && (
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="flex-1 min-w-[120px] py-1 text-[14px] leading-5 tracking-[0.14px] font-OpenSansRegular text-[#1A2332] placeholder:text-[#868DA6] outline-none bg-transparent"
        />
      )}
      {readOnly && selected.length === 0 && (
        <span className="text-[14px] leading-5 font-OpenSansRegular text-[#868DA6]">—</span>
      )}
      {matches.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-white outline outline-1 -outline-offset-1 outline-[#E5E8F3] rounded-[8px] shadow-[0px_8px_24px_rgba(16,24,40,0.12)] max-h-[200px] overflow-y-auto">
          {matches.map((o) => (
            <button
              key={o.id}
              onClick={() => {
                onAdd(o.id);
                setQuery("");
              }}
              className="w-full text-left px-3 py-2 text-[14px] leading-5 font-OpenSansSemiBold text-[#1A2332] capitalize hover:bg-[#F8F9FE]"
            >
              {o.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** White section card with a title + optional subtitle, per the drawer design. */
function SectionCard({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-[12px] outline outline-1 -outline-offset-1 outline-[#E5E8F3] p-5 flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <p className="text-[16px] leading-6 font-OpenSansSemiBold text-[#1A2332]">{title}</p>
        {subtitle && (
          <p className="text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansRegular text-[#7C829A]">{subtitle}</p>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * Edit Member drawer — right-side slide-over matching the IAM member design:
 * header (avatar, primary-role badge, email) over Basic Info, Roles, Branches,
 * Direct Policies and Inherited Policies cards.
 * Each section is gated by its own permission and saves through its own
 * gated endpoint (update_staff / assign_role / assign_branches /
 * assign_policies); only the permitted parts are sent.
 */
function EditMemberDrawer({ isOpen, user: userProp, roles = [], branches = [], onClose, onSaved }) {
  // Retain the last non-null user so the panel keeps its content while
  // sliding out — the parent clears `user` at the moment it closes.
  const [user, setUser] = useState(userProp);
  useEffect(() => {
    if (userProp) setUser(userProp);
  }, [userProp]);

  // Drives the CSS transitions. The component mounts with isOpen already
  // true, so flip this after the panel has painted in its closed position —
  // otherwise the first paint is already open and the slide-in never plays.
  // Double rAF on purpose: on the very first open the panel enters the DOM a
  // render after `user` is retained, and a single rAF fires before that first
  // paint, skipping the transition. `user` in the deps restarts the timer
  // once the panel is actually rendered.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      setVisible(false);
      return undefined;
    }
    if (!user) return undefined;
    let secondId;
    const firstId = requestAnimationFrame(() => {
      secondId = requestAnimationFrame(() => setVisible(true));
    });
    return () => {
      cancelAnimationFrame(firstId);
      if (secondId) cancelAnimationFrame(secondId);
    };
  }, [isOpen, user]);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [roleIds, setRoleIds] = useState([]);
  const [branchIds, setBranchIds] = useState([]);
  const [directPolicyIds, setDirectPolicyIds] = useState([]);
  // policyId -> branch ids its branch-scoped actions (time-off:manage) are
  // limited to; null / absent = every branch.
  const [policyScopes, setPolicyScopes] = useState({});
  const [allPolicies, setAllPolicies] = useState([]);
  const [expandedRoles, setExpandedRoles] = useState({}); // roleId -> bool
  const [showAllInherited, setShowAllInherited] = useState({}); // roleId -> bool
  const [isSaving, setIsSaving] = useState(false);

  const canEditBasic = can("user:update");
  const canEditRoles = can("user:assignRole");
  const canEditBranches = can("user:assignBranch");
  const canEditPolicies = can("user:assignPolicy");

  // Prefill + load supporting data each time the drawer opens.
  useEffect(() => {
    if (!isOpen || !user) return;
    setName(user.name ?? "");
    setEmail(user.email ?? "");
    setPhone(formatUSPhone(user.phone || ""));
    setRoleIds((user.roles ?? []).map((r) => r.id));
    setBranchIds((user.branches ?? []).map((b) => b.id));
    setDirectPolicyIds([]);
    setPolicyScopes({});
    setExpandedRoles({});
    setShowAllInherited({});
    setIsSaving(false);

    // A Super Admin resolves to `*:*` by role name, so the policy cards are
    // hidden for them and the data behind those cards is not worth fetching.
    if (isSuperAdminUser(user)) return;

    // Direct policies live on the user relation — fetch the full user
    // (user:read-gated; the Manage page implies it, but guard anyway).
    if (can("user:read")) {
      UserService.getUser(user.id)
        .then((res) => {
          const direct = res.data?.directPolicies ?? [];
          setDirectPolicyIds(direct.map((p) => p.id));
          setPolicyScopes(Object.fromEntries(direct.map((p) => [p.id, p.branchIds ?? null])));
        })
        .catch(handleError);
    }
    // Policy list: search source for Direct Policies + role → policies map
    // for the Inherited Policies card (each policy carries its role ids).
    // Uses the ungated picker endpoint so the drawer works without
    // iam:readPolicy.
    PolicyService.getForAssign()
      .then((res) => setAllPolicies(res.data ?? []))
      .catch(handleError);
  }, [isOpen, user]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const selectedRoles = useMemo(
    () => roleIds.map((id) => roles.find((r) => r.id === id)).filter(Boolean),
    [roles, roleIds],
  );

  // roleId -> policies attached to that role (inherited by its users).
  const policiesByRoleId = useMemo(() => {
    const map = {};
    for (const policy of allPolicies) {
      for (const r of policy.roles ?? []) {
        (map[r.id] = map[r.id] || []).push(policy);
      }
    }
    return map;
  }, [allPolicies]);

  // Selected direct policies that allow a branch-scoped action
  // (time-off:manage), each of which gets a branch scope picker.
  const scopablePolicies = useMemo(
    () => directPolicyIds.map((id) => allPolicies.find((p) => p.id === id)).filter((p) => p?.branchScoped),
    [allPolicies, directPolicyIds],
  );

  // Selected roles that grant time-off:manage too. A direct policy's branch
  // limit overrides their every-branch access, which the scope picker notes.
  const scopedGrantRoleNames = useMemo(
    () =>
      selectedRoles
        .filter((role) => (policiesByRoleId[role.id] ?? []).some((p) => p.branchScoped))
        .map((role) => vocabularize(role.name))
        .join(", "),
    [selectedRoles, policiesByRoleId],
  );

  // Checking branches limits the policy to them; unchecking the last one
  // returns it to every branch. Ids of since-deleted branches are dropped.
  const toggleScopeBranch = (policyId, branchId) =>
    setPolicyScopes((prev) => {
      const current = (prev[policyId] ?? []).filter((id) => branches.some((b) => b.id === id));
      const next = current.includes(branchId) ? current.filter((x) => x !== branchId) : [...current, branchId];
      return { ...prev, [policyId]: next.length ? next : null };
    });

  const resetScope = (policyId) => setPolicyScopes((prev) => ({ ...prev, [policyId]: null }));

  const removeDirectPolicy = (id) => {
    setDirectPolicyIds((prev) => prev.filter((x) => x !== id));
    setPolicyScopes((prev) => ({ ...prev, [id]: null }));
  };

  if (!user) {
    return null;
  }

  // Super Admin is the cross-tenant platform role: it is never returned in the
  // tenant's role list, so it can neither be edited here nor intersected with a
  // branch. Fall back to the raw relation so the badge still shows.
  const isSuperAdminMember = isSuperAdminUser(user);
  const primaryRole = selectedRoles[0] ?? (user.roles ?? [])[0];

  const save = () => {
    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();
    if (canEditBasic) {
      if (!trimmedName) {
        toast("Name is required");
        return;
      }
      if (trimmedPhone !== "" && !isValidUSPhone(trimmedPhone)) {
        toast("Enter a valid US phone number");
        return;
      }
    }
    if (canEditBranches && !isSuperAdminMember && branchIds.length === 0) {
      toast("User must belong to at least one branch");
      return;
    }

    // Each capability persists through its own gated endpoint — only the
    // parts the current user is allowed to edit are sent.
    const requests = [];
    if (canEditBasic) {
      requests.push(
        UserService.updateUserPartialStaff(user.id, {
          name: trimmedName,
          email: email.trim(),
          phone: trimmedPhone === "" ? "" : toE164Phone(trimmedPhone),
        }),
      );
    }
    // Roles/branches are left untouched for a Super Admin: `roles` excludes
    // their role, so sending the selection would strip it, and they need no
    // branch to reach one.
    if (canEditRoles && !isSuperAdminMember) {
      requests.push(
        UserService.assignRole(
          user.id,
          roles.filter((r) => roleIds.includes(r.id)),
        ),
      );
    }
    if (canEditBranches && !isSuperAdminMember) {
      requests.push(
        UserService.assignBranches(
          user.id,
          branches.filter((b) => branchIds.includes(b.id)),
        ),
      );
    }
    if (canEditPolicies && !isSuperAdminMember) {
      const knownBranchIds = new Set(branches.map((b) => b.id));
      requests.push(
        UserService.assignPolicies(
          user.id,
          directPolicyIds.map((id) => {
            const scope = policyScopes[id];
            // Ids of since-deleted branches are dropped; a scope left with none
            // stays an empty list (no branches) rather than widening to all.
            return { id, branchIds: Array.isArray(scope) ? scope.filter((b) => knownBranchIds.has(b)) : null };
          }),
        ),
      );
    }
    if (requests.length === 0) {
      onClose?.();
      return;
    }

    setIsSaving(true);
    Promise.all(requests)
      .then(() => {
        toast("Member updated successfully");
        onSaved?.();
        onClose?.();
      })
      .catch(handleError)
      .finally(() => setIsSaving(false));
  };

  const inputClass =
    "w-full h-10 px-3 rounded-[6px] outline outline-1 -outline-offset-1 outline-[#E5E8F3] text-[14px] leading-5 tracking-[0.14px] font-OpenSansRegular text-[#1A2332] placeholder:text-[#868DA6] focus:outline focus:outline-1 focus:-outline-offset-1 focus:outline-[#E5E8F3] disabled:bg-[#F8F9FE] disabled:text-[#868DA6]";

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black bg-opacity-20 transition-opacity duration-300 ease-in-out ${
          visible ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 right-0 bottom-0 z-50 flex h-screen w-[474px] max-w-full transform flex-col bg-white shadow-[0_4px_24px_rgba(0,0,0,0.12)] transition-transform duration-300 ease-in-out ${
          visible ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-4 flex items-start justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <MemberAvatar user={user} />
            <div className="min-w-0 flex flex-col gap-1">
              <p className="text-[18px] leading-6 font-OpenSansSemiBold text-[#1A2332] truncate">{user.name || "—"}</p>
              <div className="flex items-center gap-2 min-w-0">
                {primaryRole && (
                  <span className="inline-flex shrink-0 px-2 py-[2px] rounded-[6px] bg-[#EAF3FF] text-[#0058E5] text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansSemiBold capitalize">
                    {primaryRole.name}
                  </span>
                )}
                <span className="text-[13px] leading-[18px] font-OpenSansRegular text-[#868DA6] truncate">
                  {user.email}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-[#868DA6] hover:bg-[#F0F1F5] hover:text-[#1A2332]"
            title="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M12 4L4 12M4 4l8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-[#FBFCFF] border-t border-[#E5E8F3] px-4 py-4 flex flex-col gap-4">
          {/* Basic Info */}
          <SectionCard title="Basic Info">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332]">
                  Full Name
                </label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!canEditBasic}
                  placeholder="Full name"
                  className={inputClass}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332]">
                  Phone
                </label>
                <input
                  value={phone}
                  onChange={(e) => setPhone(formatUSPhone(e.target.value))}
                  disabled={!canEditBasic}
                  placeholder="+1 (555) 555-0100"
                  className={`${inputClass} ${
                    phone && !isValidUSPhone(phone) ? "outline-[#D93A3A] focus:outline-[#D93A3A]" : ""
                  }`}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332]">
                Email
              </label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!canEditBasic}
                placeholder="name@example.com"
                className={inputClass}
              />
            </div>
          </SectionCard>

          {/* Roles — same single-select dropdown as the Manage table */}
          <SectionCard title="Roles" subtitle="Assign existing IAM roles to this user.">
            {isSuperAdminMember ? (
              <div className="flex flex-wrap items-center gap-1">
                <span className="inline-flex px-2 py-[2px] rounded-[6px] bg-[#EAF3FF] text-[#0058E5] text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansSemiBold capitalize">
                  Super Admin
                </span>
              </div>
            ) : canEditRoles ? (
              <RoleDropDown
                user={{ ...user, roles: selectedRoles }}
                roles={roles}
                onChange={(u, role) => setRoleIds([role.id])}
              />
            ) : (
              <div className="flex flex-wrap items-center gap-1">
                {selectedRoles.length === 0 ? (
                  <span className="text-[14px] leading-5 font-OpenSansRegular text-[#868DA6]">—</span>
                ) : (
                  selectedRoles.map((r) => (
                    <span
                      key={r.id}
                      className="inline-flex px-2 py-[2px] rounded-[6px] bg-[#EAF3FF] text-[#0058E5] text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansSemiBold capitalize"
                    >
                      {r.name}
                    </span>
                  ))
                )}
              </div>
            )}
          </SectionCard>

          {/* Branches */}
          <SectionCard
            title="Branches"
            subtitle={isSuperAdminMember ? "Super Admin has access to every branch." : "Assign/remove branches."}
          >
            {isSuperAdminMember ? (
              <span className="inline-flex w-fit px-2 py-[2px] rounded-[6px] bg-[#EEF0F9] text-[#5F6477] text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansSemiBold">
                All branches
              </span>
            ) : (
              <ChipSearchInput
                options={branches}
                selectedIds={branchIds}
                onAdd={(id) => setBranchIds((prev) => [...prev, id])}
                onRemove={(id) => setBranchIds((prev) => prev.filter((x) => x !== id))}
                placeholder="Search branches…"
                variant="brand"
                readOnly={!canEditBranches}
              />
            )}
          </SectionCard>

          {/* Direct Policies / Inherited Policies — omitted for a Super Admin:
              the policy evaluator resolves that role to `*:*` by name without
              reading the policy graph, so anything attached here is ignored. */}
          {!isSuperAdminMember && (
            <SectionCard title="Direct Policies" subtitle="Additional policies assigned directly to this user.">
              {roleIds.length > 1 && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-[8px] bg-[#EAF3FF]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    className="shrink-0"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M8 14.5C11.5899 14.5 14.5 11.5899 14.5 8C14.5 4.41015 11.5899 1.5 8 1.5C4.41015 1.5 1.5 4.41015 1.5 8C1.5 11.5899 4.41015 14.5 8 14.5ZM8 4.5C8.41421 4.5 8.75 4.83579 8.75 5.25C8.75 5.66421 8.41421 6 8 6C7.58579 6 7.25 5.66421 7.25 5.25C7.25 4.83579 7.58579 4.5 8 4.5ZM8 7C8.27614 7 8.5 7.22386 8.5 7.5V11C8.5 11.2761 8.27614 11.5 8 11.5C7.72386 11.5 7.5 11.2761 7.5 11V7.5C7.5 7.22386 7.72386 7 8 7Z"
                      fill="#0065FD"
                    />
                  </svg>
                  <span className="text-[13px] leading-[18px] font-OpenSansRegular text-[#0058E5]">
                    User has elevated access from multiple assigned roles.
                  </span>
                </div>
              )}
              <ChipSearchInput
                options={allPolicies}
                selectedIds={directPolicyIds}
                onAdd={(id) => setDirectPolicyIds((prev) => [...prev, id])}
                onRemove={removeDirectPolicy}
                placeholder="Search policies…"
                variant="slate"
                readOnly={!canEditPolicies}
              />
              {/* Branch scope: limits only Manage Time Off Requests in the
                  policy — its other permissions still apply everywhere. */}
              {scopablePolicies.map((policy) => {
                const scope = policyScopes[policy.id] ?? null;
                // Scoped, but every scoped branch has since been deleted.
                const scopeEmptied = scope !== null && !scope.some((id) => branches.some((b) => b.id === id));
                return (
                  <div key={policy.id} className="flex flex-col gap-2 rounded-[8px] bg-[#F8F9FE] p-3">
                    <div className="flex flex-col gap-0.5">
                      <p className="text-[14px] leading-5 tracking-[0.14px] font-OpenSansSemiBold text-[#1A2332]">
                        Time Off Branches · {vocabularize(policy.name)}
                      </p>
                      <p className="text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansRegular text-[#7C829A]">
                        Manages time off requests only from users of the checked branches.
                      </p>
                      {scopedGrantRoleNames && (
                        <p className="text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansRegular text-[#0058E5]">
                          {scope === null
                            ? `The ${scopedGrantRoleNames} role also grants this for all branches. Check branches to limit this user.`
                            : `Overrides the all-branch access from the ${scopedGrantRoleNames} role — only the checked branches apply.`}
                        </p>
                      )}
                    </div>
                    {/* "All branches" is checked while no branch is; checking
                        it clears the branch selection. */}
                    <div className="grid grid-cols-2 gap-2 max-h-[240px] overflow-y-auto">
                      {[{ id: null, name: "All branches" }, ...branches].map((branch) => {
                        const checked = branch.id === null ? scope === null : (scope ?? []).includes(branch.id);
                        return (
                          <label
                            key={branch.id ?? "all"}
                            className={`flex items-center gap-2 px-3 py-2 rounded-[6px] bg-white outline outline-1 -outline-offset-1 ${
                              checked
                                ? "outline-[#0065FD]"
                                : `outline-[#E5E8F3] ${canEditPolicies ? "hover:outline-[#B9C4E0]" : ""}`
                            } ${canEditPolicies ? "cursor-pointer" : "cursor-default"} ${
                              branch.id === null ? "col-span-2" : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={!canEditPolicies}
                              onChange={() =>
                                branch.id === null ? resetScope(policy.id) : toggleScopeBranch(policy.id, branch.id)
                              }
                              className="accent-[#0065FD] w-4 h-4 shrink-0"
                            />
                            <span
                              className={`text-[14px] leading-5 tracking-[0.14px] capitalize truncate ${
                                checked ? "font-OpenSansSemiBold text-[#0065FD]" : "font-OpenSansRegular text-[#1A2332]"
                              }`}
                            >
                              {branch.name}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    {scopeEmptied && (
                      <p className="text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansRegular text-[#D93A3A]">
                        The branches this was limited to were deleted, so it currently applies to no branch.
                      </p>
                    )}
                  </div>
                );
              })}
            </SectionCard>
          )}

          {!isSuperAdminMember && (
            <SectionCard title="Inherited Policies" subtitle="Policies inherited automatically from assigned roles.">
              <div className="flex flex-col gap-2">
                {selectedRoles.length === 0 ? (
                  <p className="text-[13px] font-OpenSansRegular text-[#868DA6]">No roles assigned.</p>
                ) : (
                  selectedRoles.map((role) => {
                    const rolePolicies = policiesByRoleId[role.id] ?? [];
                    const isExpanded = !!expandedRoles[role.id];
                    const showAll = !!showAllInherited[role.id];
                    const visible = showAll ? rolePolicies : rolePolicies.slice(0, INHERITED_PREVIEW_COUNT);
                    const hidden = rolePolicies.length - visible.length;
                    return (
                      <div
                        key={role.id}
                        className="rounded-[8px] outline outline-1 -outline-offset-1 outline-[#E5E8F3] overflow-hidden"
                      >
                        <button
                          onClick={() => setExpandedRoles((prev) => ({ ...prev, [role.id]: !isExpanded }))}
                          className="w-full flex items-center justify-between px-4 py-3 bg-[#F8F9FE]"
                        >
                          <span className="flex items-center gap-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                              className={`text-[#868DA6] transition-transform ${isExpanded ? "" : "-rotate-90"}`}
                            >
                              <path
                                d="M3.5 5.25L7 8.75l3.5-3.5"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <span className="text-[14px] leading-5 font-OpenSansSemiBold text-[#1A2332] capitalize">
                              {vocabularize(role.name)} Role
                            </span>
                          </span>
                          <span className="inline-flex px-2 py-[2px] rounded-full bg-[#EEF0F9] text-[#5F6477] text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansSemiBold">
                            {rolePolicies.length} {rolePolicies.length === 1 ? "Policy" : "Policies"}
                          </span>
                        </button>
                        {isExpanded && (
                          <div className="px-4 py-3 flex flex-col gap-2 border-t border-[#E5E8F3]">
                            {rolePolicies.length === 0 ? (
                              <span className="text-[13px] font-OpenSansRegular text-[#868DA6]">
                                No policies attached to this role.
                              </span>
                            ) : (
                              <>
                                {visible.map((p) => (
                                  <span key={p.id} className="flex items-center gap-2">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="14"
                                      height="14"
                                      viewBox="0 0 14 14"
                                      fill="none"
                                      className="shrink-0"
                                    >
                                      <path
                                        d="M11.667 3.5L5.25 9.917 2.333 7"
                                        stroke="#22C55E"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                    <span className="text-[14px] leading-5 font-OpenSansRegular text-[#1A2332]">
                                      {p.name}
                                    </span>
                                  </span>
                                ))}
                                {hidden > 0 && (
                                  <button
                                    onClick={() => setShowAllInherited((prev) => ({ ...prev, [role.id]: true }))}
                                    className="self-start text-[14px] leading-5 font-OpenSansSemiBold text-[#0065FD] hover:underline"
                                  >
                                    Show {hidden} More…
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </SectionCard>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#E5E8F3] flex items-center gap-3">
          <button
            onClick={onClose}
            className="flex-1 h-10 rounded-[8px] bg-white outline outline-1 -outline-offset-1 outline-[#C9CEE2] text-[16px] leading-6 capitalize font-OpenSansSemiBold text-[#1A2332] hover:bg-[#F8F9FE]"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={isSaving}
            className="flex-1 h-10 flex items-center justify-center rounded-[8px] bg-[#0065FD] text-white text-[16px] leading-6 capitalize font-OpenSansSemiBold hover:bg-[#0048bd] disabled:opacity-60"
          >
            {isSaving ? <Loader variant="button" ariaLabel="saving-member" /> : "Save Changes"}
          </button>
        </div>
      </div>
    </>
  );
}

export default EditMemberDrawer;
